/**
 * Sticky add to cart bar (mobile only).
 *
 * Shows when ALL of these are true:
 *   - viewport is mobile
 *   - the customer has scrolled past a small threshold (so it never shows on the
 *     first view, and hides again when they return to the very top)
 *   - the main add to cart button is off screen
 *
 * The sticky button mirrors the main add to cart button (text, disabled state,
 * loading spinner) and replays a click on it, so cart / engraving / app
 * behaviour all stay in the theme's single existing path.
 */
(function () {
  'use strict';

  if (window.__ptalStickyAtcLoaded) return;
  window.__ptalStickyAtcLoaded = true;

  var MOBILE_QUERY = '(max-width: 768px)';
  // Below this scroll position the bar stays hidden. Covers "don't show on first
  // view" and "hide again when scrolled back to the top".
  var SCROLL_THRESHOLD = 60;
  // Shortest time the add to cart spinner stays up, so a fast cart response
  // still reads as feedback rather than a flicker.
  var MIN_LOADING_MS = 600;

  function StickyAtc(container) {
    this.container = container;
    this.mainAtc = container.querySelector('[data-add-to-cart]');
    this.mainAtcText = container.querySelector('[data-add-to-cart-text]');

    this.sticky = container.querySelector('[data-sticky-atc]');
    this.stickyBtn = container.querySelector('[data-sticky-atc-btn]');
    this.stickyBtnText = container.querySelector('[data-sticky-atc-text]');
    this.vartitle = container.querySelector('[data-sticky-vartitle]');
    this.stickyPrice = container.querySelector('[data-sticky-price]');
    this.stickyCompare = container.querySelector('[data-sticky-compare]');
    this.stickyBadge = container.querySelector('[data-sticky-badge]');

    this.priceEl = container.querySelector('[data-product-price]');
    this.compareEl = container.querySelector('[data-compare-price]');

    this.scrolledPastThreshold = false;
    this.atcVisible = true;

    if (!this.sticky || !this.mainAtc) return;

    this.bindEvents();
    this.syncStickyButton();
    this.initSticky();
    this.watchLoadingState();
  }

  StickyAtc.prototype = {
    /** The sticky button mirrors the main button's text and disabled state. */
    syncStickyButton: function () {
      if (!this.stickyBtn || !this.mainAtc) return;

      this.stickyBtn.disabled = this.mainAtc.disabled;
      this.stickyBtn.classList.toggle('disabled', this.mainAtc.classList.contains('disabled'));
      if (this.stickyBtnText && this.mainAtcText) {
        this.stickyBtnText.textContent = this.mainAtcText.textContent.trim();
      }
    },

    /**
     * The bar is fixed, so it has no place in the document and would cover the end
     * of the footer once the page bottoms out. Publish its measured height as
     * --stickyAtcHeight so the footer can reserve matching space. Measured rather
     * than hard coded because the height changes: long prices wrap onto extra lines.
     * offsetHeight is 0 on desktop (the bar is display:none), which zeroes the
     * reserved space there.
     */
    measureStickyHeight: function () {
      if (!this.sticky) return;
      document.documentElement.style.setProperty(
        '--stickyAtcHeight',
        this.sticky.offsetHeight + 'px'
      );
    },

    /**
     * The theme drops .btn--loading on the main add to cart button while the ajax
     * add is in flight (theme.AjaxProduct) and takes it off when the cart responds.
     * The sticky button replays that main button, which is off screen at the time,
     * so mirror the class across to show the same spinner on whichever button the
     * customer actually tapped. The main button stays the single source of truth,
     * so there is no way to leave a spinner stuck on.
     */
    mirrorLoadingState: function () {
      if (!this.stickyBtn || !this.mainAtc) return;
      this.stickyBtn.classList.toggle(
        'btn--loading',
        this.mainAtc.classList.contains('btn--loading')
      );
    },

    /** Toggle .btn--loading without our own observer reacting to it. */
    setLoading: function (on) {
      if (this.loadingObserver) this.loadingObserver.disconnect();
      this.mainAtc.classList.toggle('btn--loading', on);
      this.mirrorLoadingState();
      if (this.loadingObserver) {
        this.loadingObserver.observe(this.mainAtc, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    },

    watchLoadingState: function () {
      if (!this.mainAtc || typeof MutationObserver === 'undefined') return;

      var self = this;

      this.loadingObserver = new MutationObserver(function () {
        var loading = self.mainAtc.classList.contains('btn--loading');

        if (loading) {
          self.loadingStartedAt = Date.now();
          self.mirrorLoadingState();
          return;
        }

        // The theme cleared the spinner. /cart/add.js usually answers in ~200ms
        // and the drawer opens straight away, so the spinner would flash by too
        // fast to read as feedback. Hold it just long enough to be seen.
        if (self.loadingStartedAt) {
          var remaining = MIN_LOADING_MS - (Date.now() - self.loadingStartedAt);
          self.loadingStartedAt = 0;

          if (remaining > 0) {
            self.setLoading(true);
            clearTimeout(self.loadingTimer);
            self.loadingTimer = setTimeout(function () {
              self.setLoading(false);
            }, remaining);
            return;
          }
        }

        self.mirrorLoadingState();
      });

      this.loadingObserver.observe(this.mainAtc, {
        attributes: true,
        attributeFilter: ['class']
      });
      this.mirrorLoadingState();
    },

    /** Copy the main price block into the sticky bar and recompute the % off badge. */
    syncStickyPrice: function () {
        var selectedInput = document.querySelector('.variant-wrapper input[data-variant-input]:checked');
        var selectedValue = selectedInput ? selectedInput.value : null;
        this.vartitle.innerHTML = selectedValue;

      if (!this.stickyPrice || !this.priceEl) return;

      this.stickyPrice.innerHTML = this.priceEl.innerHTML;

      var price = parseInt(this.priceEl.getAttribute('val'), 10);
      var compare = this.compareEl ? parseInt(this.compareEl.getAttribute('val'), 10) : 0;
      var onSale = !!compare && !!price && compare > price;

      if (this.stickyCompare) {
        this.stickyCompare.innerHTML = onSale ? this.compareEl.innerHTML : '';
        this.stickyCompare.classList.toggle('hide', !onSale);
      }

      if (this.stickyBadge) {
        if (onSale) {
          this.stickyBadge.textContent = Math.round(((compare - price) * 100) / compare) + '% OFF';
        }
        this.stickyBadge.classList.toggle('hide', !onSale);
      }
    },

    bindEvents: function () {
      var self = this;

      // The sticky button just replays the real add to cart button
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', function (evt) {
          evt.preventDefault();
          if (self.mainAtc && !self.mainAtc.disabled) self.mainAtc.click();
        });
      }

      // Keep the sticky button and price in step after a variant change
      var onVariantChange = function () {
        setTimeout(function () {
          self.syncStickyButton();
          self.syncStickyPrice();
        }, 0);
      };
      this.container.addEventListener('variantChange', onVariantChange);
      document.addEventListener('variant:change', onVariantChange);
      this.container.addEventListener('change', function (evt) {
        if (evt.target.matches('[data-variant-input]')) onVariantChange();
      });
    },

    initSticky: function () {
      var self = this;
      this.mql = window.matchMedia(MOBILE_QUERY);
      this.syncStickyPrice();
      this.measureStickyHeight();

      // The bar's height changes with its content (wrapped prices) and at the
      // breakpoint, so keep the reserved footer space in step with it.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.measureStickyHeight.bind(this));
        this.resizeObserver.observe(this.sticky);
      } else {
        window.addEventListener('resize', this.measureStickyHeight.bind(this));
      }

      // Condition 1: scrolled past the threshold. Kept live (not one-shot) so the
      // bar hides again when the customer scrolls back up into the 0-threshold zone.
      var onScroll = function () {
        var past = window.pageYOffset > SCROLL_THRESHOLD;
        if (past !== self.scrolledPastThreshold) {
          self.scrolledPastThreshold = past;
          self.updateStickyVisibility();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Condition 2: the main add to cart button is off screen
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            self.atcVisible = entries[0].isIntersecting;
            self.updateStickyVisibility();
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.observer.observe(this.mainAtc);
      }

      var onMqChange = function () {
        self.updateStickyVisibility();
        self.measureStickyHeight();
      };
      if (this.mql.addEventListener) this.mql.addEventListener('change', onMqChange);
      else if (this.mql.addListener) this.mql.addListener(onMqChange);
    },

    updateStickyVisibility: function () {
      if (!this.sticky) return;

      var show = this.mql.matches && this.scrolledPastThreshold && !this.atcVisible;

      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Lets the floating chat button move out of the way
      document.body.classList.toggle('sticky-atc-visible', show);
    }
  };

  function init() {
    document.querySelectorAll('[data-section-type="product"]').forEach(function (container) {
      if (container.__stickyAtc) return;
      container.__stickyAtc = new StickyAtc(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections
  document.addEventListener('shopify:section:load', init);
})();
